import React, { BaseSyntheticEvent, useContext } from "react";
import Big from "big.js";

import {
  formatDate,
  toUpper,
  renameMainCurrency,
} from "@features/utils/functions";
import { classNames } from "@features/utils/reactFunctions";
import { FundsAppContext } from "@features/context";

import ZebraDetailsCard from "@appComponents/ZebraDetailsCard/ZebraDetailsCard";
import CurrencyElement from "@appComponents/CurrencyElement/CurrencyElement";
import Loader from "@appComponents/Loader/Loader";
import ConfirmForm from "@appComponents/ConfirmForm/ConfirmForm";
import FundsService from "@services/Funds.service";

import { ETransactionType } from "@funds/enums/ETransactionType";
import IInOutHistoryRow, { TInOutHistoryRow } from "./IInOutHistoryRow";

import * as style from "./InOutHistoryRowStyle.module.scss";
import * as elements from "@appStyles/_elements.module.scss";

export const labels = {
  id: "ID",
  currency: "Currency",
  amount: "Amount",
  created_at: "Date",
  status: "Status",
  accountable: "Destination Details",
  balance_before: "Balance Before",
  income_currency: "Income Currency",
  outcome_currency: "Outcome Currency",
  income_balance: "Income Balance",
  outcome_balance: "Outcome Balance",
  after: "After",
  before: "Before",
  balance_after: "Balance After",
  network: "Network",
  fee: "Fee",
  address: "Address",
  type: "Type",
  country: "Country",
  holder_name: "Beneficiary Name",
  bank_address: "Bank Address",
  bank_name: "Bank Name",
  account_number: "Account Number",
  aba: "ABA",
  sort_code: "Sort Code",
  iban: "IBAN",
  bic: "BIC",
  institution_number: "Institution Number",
  branch_code: "Branch Code",
  name: "Wallet Name",
  payment: "Payment Details",
  exchange_rate: "Exchange Rate",
  tx_id: "Transaction ID",
};

const InOutHistoryRow: React.FunctionComponent<IInOutHistoryRow> = React.memo(
  (props) => {
    const { inOutItem, handleCancel, transactionType } = props;
    const [expandedState, setExpandedState] = React.useState<boolean>(false);
    const [showLightBoxState, setShowLightBoxState] =
      React.useState<boolean>(false);
    const [detailsState, setDetailsState] =
      React.useState<TInOutHistoryRow | null>(null);
    const { investorId } = useContext(FundsAppContext);

    const handleChange = () => {
      if (transactionType === ETransactionType.deposit) {
        return;
      }

      setExpandedState((prevState) => !prevState);

      if (transactionType === ETransactionType.withdraw) {
        return setDetailsForWithdraw();
      }

      if (transactionType === ETransactionType.purchase) {
        return setDetailsForPurchase();
      }

      setDetailsForCryptoTrade();
    };

    const setDetailsForCryptoTrade = () => {
      if (!detailsState) {
        FundsService.getCryptoTradingHistoryDetails(inOutItem.id).then(
          (res) => {
            setDetailsState({
              id: res.data.id,
              exchange_rate: res.data.exchange_rate,
              fee: res.data.fee,
              income_balance: res.data.income_balance,
              outcome_balance: res.data.outcome_balance,
            });
          }
        );
      }
    };

    const setDetailsForWithdraw = () => {
      if (!detailsState) {
        if (process.env.ENABLE_BINANCE_TRADING) {
          investorId &&
            FundsService.getWithdrawsHistoryDetails(
              investorId,
              inOutItem.id
            ).then((res) => {
              setDetailsState({
                amount: res.data.amount,
                balance_after: res.data.balance_after,
                balance_before: res.data.balance_before,
                created_at: res.data.created_at,
                currency: res.data.currency,
                fee: res.data.fee,
                id: res.data.id,
                status: res.data.status,
                tx_id: res.data.tx_id,
                tx_url: res.data.tx_url,
                accountable: res.data.accountable,
              });
            });
        } else {
          FundsService.oldGetWithdrawsHistoryDetails(inOutItem.id).then(
            (res) => {
              setDetailsState({
                amount: res.data.amount,
                balance_after: res.data.balance_after,
                balance_before: res.data.balance_before,
                created_at: res.data.created_at,
                currency: res.data.currency,
                fee: res.data.fee,
                id: res.data.id,
                status: res.data.status,
                accountable: res.data.accountable,
              });
            }
          );
        }
      }
    };

    const setDetailsForPurchase = () => {
      if (!detailsState) {
        FundsService.getPurchasesHistoryDetails(inOutItem.id).then((res) => {
          setDetailsState({
            amount: res.data.amount,
            balance_after: res.data.balance_after,
            balance_before: res.data.balance_before,
            created_at: res.data.created_at,
            currency: res.data.currency,
            id: res.data.id,
            status: res.data.status,
            exchange_rate: res.data.exchange_rate,
            fee: res.data.fee,
            payment: res.data.payment,
          });
        });
      }
    };

    const initiateCancel = (e: BaseSyntheticEvent) => {
      e.stopPropagation();
      setShowLightBoxState(true);
    };

    const cancelWithdraw = (withdrawId: number) => {
      setShowLightBoxState(false);
      handleCancel(withdrawId);
    };

    const formatValue = (
      label: string,
      value: React.ReactText,
      parent: string,
      currency: string
    ): string | number | boolean | JSX.Element => {
      switch (label) {
        case "Currency":
          return renameMainCurrency(value.toString()).toUpperCase();
        case "Before":
        case "After":
          return (
            <CurrencyElement
              withCurrencyLabel={false}
              value={value}
              currency={
                detailsState![
                  parent === "Income Balance"
                    ? "income_balance"
                    : "outcome_balance"
                ]!["currency"]
              }
            />
          );
        case "Country":
        case "Network":
          return value.toString().toUpperCase();
        case "Date":
          return typeof value === "string" && formatDate(value);
        case "Status":
          return toUpper(value.toString());
        case "Type":
          return typeof value === "string" && toUpper(value);
        case "Exchange Rate":
          return Big(value as string)
            .round(10, 2)
            .toString();
        case "Amount":
        case "Fee":
          // Only to display fee value as free
          if (value === null) return "Free";

          return (
            <CurrencyElement
              value={value as string}
              // TODO: Remove fallback when backend starts pass fee as object
              currency={currency || detailsState!["currency"]!}
              withCurrencyLabel={false}
            />
          );
        case "Transaction ID":
          return detailsState?.tx_url ? (
            <a
              href={detailsState.tx_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {value}
            </a>
          ) : (
            value
          );
        default:
          return value;
      }
    };

    const controls = () => {
      if (transactionType !== ETransactionType.deposit) {
        return (
          <>
            {canBeCancelled() && !expandedState && (
              <button
                className={classNames(
                  elements.cancelButton,
                  style.dragRightCancel
                )}
                onClick={initiateCancel}
                title="Cancel"
              />
            )}
            <div
              className={classNames(
                elements.arrowIcon,
                iconClass,
                style.dragRightArrow
              )}
              title="More Details"
            />
          </>
        );
      }

      return null;
    };

    const canBeCancelled = () => {
      return (
        transactionType === ETransactionType.withdraw &&
        (inOutItem.status === "pending" || inOutItem.status === "request")
      );
    };

    const iconClass = expandedState ? "fa fa-angle-down" : "fa fa-angle-right";

    return (
      <>
        <div
          data-cy={`${inOutItem.id}-transaction-history-item`}
          key={inOutItem.id}
          className={elements.zebraTableRow}
          onClick={handleChange}
        >
          <div
            className={classNames(
              elements.zebraTableItem,
              style.zebraTableItemFirst
            )}
          >
            {formatDate(inOutItem.created_at)}
          </div>
          {[ETransactionType.tradeIn, ETransactionType.tradeOut].includes(
            transactionType
          ) ? (
            <>
              <div
                className={classNames(
                  elements.zebraTableItem,
                  style.zebraTableItemMiddle
                )}
              >
                {inOutItem.income_amount && inOutItem.income_currency && (
                  <CurrencyElement
                    value={inOutItem.income_amount}
                    currency={inOutItem.income_currency}
                  />
                )}
              </div>
              <div
                className={classNames(
                  elements.zebraTableItem,
                  style.zebraTableItemLast
                )}
              >
                {inOutItem.outcome_amount && inOutItem.outcome_currency && (
                  <CurrencyElement
                    value={inOutItem.outcome_amount}
                    currency={inOutItem.outcome_currency}
                  />
                )}
                {controls()}
              </div>
            </>
          ) : (
            <>
              <div
                className={classNames(
                  elements.zebraTableItem,
                  style.zebraTableItemMiddle
                )}
              >
                {inOutItem.status ? toUpper(inOutItem.status) : "Unavailable"}
              </div>
              <div
                className={classNames(
                  elements.zebraTableItem,
                  style.zebraTableItemLast
                )}
              >
                {inOutItem.amount && inOutItem.currency && (
                  <CurrencyElement
                    value={inOutItem.amount}
                    currency={inOutItem.currency}
                  />
                )}
                {controls()}
              </div>
            </>
          )}
          {expandedState &&
            (detailsState ? (
              <ZebraDetailsCard
                formatValue={formatValue}
                data={detailsState}
                labels={labels}
              >
                {canBeCancelled() && (
                  <div onClick={initiateCancel} className={style.cancel}>
                    Cancel
                  </div>
                )}
              </ZebraDetailsCard>
            ) : (
              <div className={style.stretchLoader}>
                <Loader />
              </div>
            ))}
        </div>
        <ConfirmForm
          closeLightbox={() => setShowLightBoxState(false)}
          actionFunction={() => cancelWithdraw(inOutItem.id)}
          values={{
            headline: "Are you sure you wish to cancel your withdraw?",
            paragraph: `Please note that this operation is irreversible.`,
            actionButton: "Confirm",
          }}
          isVisible={showLightBoxState}
        />
      </>
    );
  }
);

export default InOutHistoryRow;
